import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Button } from '../ui/button'
import { AppIcon } from '../ui/icon'
import { mdiCheck, mdiClose, mdiAccountTie  } from '@mdi/js'
import { apiClient } from '../../api/client'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog'
import { SECTION_ICONS } from '../../lib/section-icons'

interface StakeholderSuggestion {
  name: string
  entity_type: 'person' | 'company'
  roles?: string[]
  email?: string
  phone?: string
  siren?: string
  address?: string
  confidence: 'high' | 'medium' | 'low'
}

interface ContractSuggestion {
  reference?: string
  name: string
  contract_type?: string
  supplier_name?: string
  client_name?: string
  signed?: boolean
  start_date?: string
  end_date?: string
  annual_amount?: number
  currency?: string
  amends_reference?: string
  confidence: 'high' | 'medium' | 'low'
}

interface EntitySuggestion {
  id: string
  kind: 'stakeholder' | 'contract'
  stakeholder?: StakeholderSuggestion
  contract?: ContractSuggestion
}

interface EntitySuggestionsResponse {
  document_uid: string
  total: number
  suggestions: EntitySuggestion[]
}

interface Props {
  org: string
  documentUid: string
}

// DocumentEntitySuggestions (spec 2026-07-11-04 §2): review surface for the
// medium/low-confidence stakeholders/contracts the entity-extraction job
// parked on document.properties.entity_suggestions. Confirm promotes the
// suggestion through the job's dedup path (attaching to an existing
// stakeholder/contract instead of twinning); dismiss just drops it.
export function DocumentEntitySuggestions({ org, documentUid }: Props) {
  const { t } = useTranslation('documents')
  const queryClient = useQueryClient()
  const queryKey = useMemo(
    () => ['document-entity-suggestions', org, documentUid],
    [org, documentUid],
  )

  const { data, isLoading } = useQuery<EntitySuggestionsResponse>({
    queryKey,
    queryFn: async () => {
      const { data: resp, error } = await apiClient.GET(
        '/api/v1/organizations/{orgCode}/documents/{documentUid}/entity-suggestions',
        { params: { path: { orgCode: org, documentUid } } } as any,
      )
      if (error) throw new Error(typeof error === 'string' ? error : JSON.stringify(error))
      return resp as EntitySuggestionsResponse
    },
    staleTime: 1000 * 60 * 5,
  })

  const [processing, setProcessing] = useState<Set<string>>(new Set())
  const [confirmDismiss, setConfirmDismiss] = useState<EntitySuggestion | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [errorNotice, setErrorNotice] = useState<string | null>(null)

  const refresh = useCallback(() => {
    queryClient.invalidateQueries({ queryKey })
    // The suggestions live on document.properties — refresh the document
    // itself plus the lists a confirmation can grow.
    queryClient.invalidateQueries({
      predicate: query => {
        const qk = query.queryKey as any[]
        if (qk.length >= 2 && qk[0] === 'get') {
          const path = qk[1]
          if (
            path === '/api/v1/organizations/{orgCode}/documents/{documentUid}' ||
            path === '/api/v1/organizations/{orgCode}/contracts' ||
            path === '/api/v1/organizations/{orgCode}/users'
          ) {
            const params = qk.find(
              item => item && typeof item === 'object' && 'params' in item,
            )?.params
            if (params?.path?.orgCode === org) {
              return path.includes('{documentUid}') ? params?.path?.documentUid === documentUid : true
            }
          }
        }
        return false
      },
    })
  }, [queryClient, queryKey, org, documentUid])

  const withProcessing = useCallback(
    async (id: string, fn: () => Promise<void>) => {
      setProcessing(prev => new Set(prev).add(id))
      setNotice(null)
      setErrorNotice(null)
      try {
        await fn()
        refresh()
      } finally {
        setProcessing(prev => {
          const s = new Set(prev)
          s.delete(id)
          return s
        })
      }
    },
    [refresh],
  )

  const confirmSuggestion = useCallback(
    (s: EntitySuggestion) =>
      withProcessing(s.id, async () => {
        const { data: resp, error } = await apiClient.POST(
          '/api/v1/organizations/{orgCode}/documents/{documentUid}/entity-suggestions/{suggestionId}/confirm',
          { params: { path: { orgCode: org, documentUid, suggestionId: s.id } } } as any,
        )
        if (error) {
          setErrorNotice(t('entity_suggestions.confirm_error'))
          return
        }
        const created = (resp as any)?.created
        setNotice(
          created
            ? t('entity_suggestions.confirmed_created')
            : t('entity_suggestions.confirmed_attached'),
        )
      }),
    [withProcessing, org, documentUid, t],
  )

  const dismissSuggestion = useCallback(
    (s: EntitySuggestion) =>
      withProcessing(s.id, async () => {
        const { error } = await apiClient.DELETE(
          '/api/v1/organizations/{orgCode}/documents/{documentUid}/entity-suggestions/{suggestionId}',
          { params: { path: { orgCode: org, documentUid, suggestionId: s.id } } } as any,
        )
        if (error) {
          setErrorNotice(t('entity_suggestions.dismiss_error'))
          return
        }
        setNotice(t('entity_suggestions.dismissed'))
      }),
    [withProcessing, org, documentUid, t],
  )

  if (isLoading || !data) return null
  if (data.total === 0 && !notice) return null

  const suggestionLabel = (s: EntitySuggestion) =>
    s.kind === 'stakeholder' ? s.stakeholder?.name || s.id : s.contract?.name || s.contract?.reference || s.id

  const confidenceClass = (confidence: string) =>
    confidence === 'medium'
      ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300'
      : 'bg-muted text-muted-foreground'

  return (
    <>
      <div
        className="space-y-3 mb-6 border border-border rounded-lg p-4 bg-primary/5"
        data-testid="document-entity-suggestions"
      >
        <div>
          <h3 className="text-sm font-semibold text-foreground">
            {t('entity_suggestions.title')}
          </h3>
          <p className="text-xs text-muted-foreground">
            {t('entity_suggestions.summary', { count: data.total })}
          </p>
        </div>

        {notice && (
          <p className="text-xs text-green-700 dark:text-green-400" data-testid="entity-suggestion-notice">
            {notice}
          </p>
        )}
        {errorNotice && (
          <p className="text-xs text-destructive" data-testid="entity-suggestion-error">
            {errorNotice}
          </p>
        )}

        <div className="space-y-1">
          {data.suggestions.map(s => (
            <div
              key={s.id}
              className="flex items-start gap-2 px-3 py-2 border-b last:border-b-0 border-border/50"
              data-testid={`entity-suggestion-row-${s.id}`}
            >
              <AppIcon tone="muted" path={s.kind === 'stakeholder' ? mdiAccountTie : SECTION_ICONS.contracts} size="sm" className="mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs text-muted-foreground uppercase tracking-wide">
                    {t(`entity_suggestions.kind_${s.kind}`)}
                  </span>
                  <span className="text-sm font-medium text-foreground truncate">
                    {suggestionLabel(s)}
                  </span>
                  <span
                    className={`text-[10px] px-1.5 py-0.5 rounded-full ${confidenceClass(
                      s.kind === 'stakeholder' ? s.stakeholder?.confidence || 'low' : s.contract?.confidence || 'low',
                    )}`}
                  >
                    {t(
                      `entity_suggestions.confidence_${
                        s.kind === 'stakeholder' ? s.stakeholder?.confidence || 'low' : s.contract?.confidence || 'low'
                      }`,
                    )}
                  </span>
                </div>
                <div className="text-xs text-muted-foreground mt-0.5 space-x-2">
                  {s.kind === 'stakeholder' && s.stakeholder && (
                    <>
                      <span>{t(`entity_suggestions.entity_type_${s.stakeholder.entity_type}`)}</span>
                      {s.stakeholder.roles && s.stakeholder.roles.length > 0 && (
                        <span>{s.stakeholder.roles.join(', ')}</span>
                      )}
                      {s.stakeholder.siren && (
                        <span>
                          {t('entity_suggestions.siren')} {s.stakeholder.siren}
                        </span>
                      )}
                      {s.stakeholder.email && <span>{s.stakeholder.email}</span>}
                      {s.stakeholder.phone && <span>{s.stakeholder.phone}</span>}
                    </>
                  )}
                  {s.kind === 'contract' && s.contract && (
                    <>
                      {s.contract.reference && (
                        <span>
                          {t('entity_suggestions.reference')} {s.contract.reference}
                        </span>
                      )}
                      {s.contract.contract_type && <span>{s.contract.contract_type}</span>}
                      {s.contract.supplier_name && (
                        <span>
                          {t('entity_suggestions.supplier')} {s.contract.supplier_name}
                        </span>
                      )}
                      {s.contract.client_name && (
                        <span>
                          {t('entity_suggestions.client')} {s.contract.client_name}
                        </span>
                      )}
                      {(s.contract.start_date || s.contract.end_date) && (
                        <span>
                          {s.contract.start_date || '…'} → {s.contract.end_date || '…'}
                        </span>
                      )}
                      {typeof s.contract.annual_amount === 'number' && s.contract.annual_amount > 0 && (
                        <span>
                          {s.contract.annual_amount} {s.contract.currency || ''}
                        </span>
                      )}
                    </>
                  )}
                </div>
              </div>
              <div className="flex gap-1 flex-shrink-0">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 text-green-600 hover:text-green-700 hover:bg-green-50"
                  onClick={() => confirmSuggestion(s)}
                  disabled={processing.has(s.id)}
                  aria-label={t('entity_suggestions.confirm')}
                  title={t('entity_suggestions.confirm')}
                  data-testid={`entity-suggestion-confirm-${s.id}`}
                >
                  <AppIcon path={mdiCheck} size="xs" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 text-destructive hover:text-destructive hover:bg-destructive/10"
                  onClick={() => setConfirmDismiss(s)}
                  disabled={processing.has(s.id)}
                  aria-label={t('entity_suggestions.dismiss')}
                  title={t('entity_suggestions.dismiss')}
                  data-testid={`entity-suggestion-dismiss-${s.id}`}
                >
                  <AppIcon path={mdiClose} size="xs" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <AlertDialog open={confirmDismiss !== null} onOpenChange={open => { if (!open) setConfirmDismiss(null) }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('entity_suggestions.dismiss_confirm_title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('entity_suggestions.dismiss_confirm_body', {
                name: confirmDismiss ? suggestionLabel(confirmDismiss) : '',
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="entity-suggestion-dismiss-cancel">
              {t('entity_suggestions.dismiss_cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (confirmDismiss) dismissSuggestion(confirmDismiss)
                setConfirmDismiss(null)
              }}
              data-testid="entity-suggestion-dismiss-confirm"
            >
              {t('entity_suggestions.dismiss_confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
